import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Cross-module references surface (T-0083 / E08-F07).
 *
 * Each row is a LIVE pointer from a readiness criterion (or a per-criterion
 * score) into a source entity in E06 (deviation / deliverable) or E07
 * (finding / domain_rating). The list endpoint resolves each reference to
 * its CURRENT source-entity projection — frozen copies live only inside
 * the snapshot body (ADR-009).
 *
 * Health & Readiness consumes this for a criterion's evidence list (the
 * xref tree) and a domain's "read by criteria". Leaf clicks navigate to
 * the target module:
 *   - source_module='contract_compliance' →
 *       deviation       → /engagements/:id/contract/deviations
 *       deliverable     → /engagements/:id/contract/deliverables
 *   - source_module='health_check' →
 *       finding         → /engagements/:id/health-readiness?item=findings
 *       domain_rating   → /engagements/:id/health-readiness?item=domain:<id>
 */

export const SourceModuleSchema = z.enum(['contract_compliance', 'health_check']);
export type SourceModule = z.infer<typeof SourceModuleSchema>;

export const SourceEntityKindSchema = z.enum([
  'deviation',
  'deliverable',
  'finding',
  'domain_rating',
]);
export type SourceEntityKind = z.infer<typeof SourceEntityKindSchema>;

export const ResolvedSourceSchema = z
  .object({
    status: z.enum(['ok', 'missing']),
    kind: SourceEntityKindSchema,
    title: z.string().nullable().optional(),
    severity: z.string().nullable().optional(),
    status_value: z.string().nullable().optional(),
    rating: z.string().nullable().optional(),
    rollup_color: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
  })
  .passthrough();
export type ResolvedSource = z.infer<typeof ResolvedSourceSchema>;

export const CrossModuleReferenceItemSchema = z.object({
  ref_id: z.string(),
  criterion_id: z.string(),
  source_module: SourceModuleSchema,
  source_entity_kind: SourceEntityKindSchema,
  source_entity_id: z.string(),
  is_live: z.boolean(),
  created_at: z.string(),
  resolved: ResolvedSourceSchema,
  as_of: z.string(),
});
export type CrossModuleReferenceItem = z.infer<typeof CrossModuleReferenceItemSchema>;

export const CrossModuleReferenceListSchema = z.object({
  items: z.array(CrossModuleReferenceItemSchema),
  next_cursor: z.string().nullable(),
});
export type CrossModuleReferenceList = z.infer<typeof CrossModuleReferenceListSchema>;

export interface ListCrossModuleReferencesOpts {
  criterion_id?: string;
  source_module?: SourceModule;
  source_entity_kind?: SourceEntityKind;
  cursor?: string;
  limit?: number;
}

export function listCrossModuleReferences(
  engagementId: string,
  opts: ListCrossModuleReferencesOpts = {},
): Promise<CrossModuleReferenceList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/cross-module-refs`,
    CrossModuleReferenceListSchema,
    { query: opts as Record<string, string | number | boolean | undefined> },
  );
}

/**
 * Live cross-module references. Each item resolves to the CURRENT projection
 * (deviation severity, deliverable status, finding rating, domain rollup).
 * Vanished source entities surface as `resolved.status='missing'`.
 *
 * The snapshot-frozen variant lives inside the readiness snapshot's
 * `frozen_state.cross_module_references[]` — not this endpoint. The xref
 * screen always shows the LIVE view; the snapshot detail screen shows the
 * frozen view per ADR-009.
 */
export function useCrossModuleReferencesQuery(
  engagementId: string | undefined,
  opts: ListCrossModuleReferencesOpts = {},
) {
  return useQuery({
    queryKey: ['readiness', 'xref', engagementId, opts] as const,
    queryFn: () => listCrossModuleReferences(engagementId!, opts),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}

// ===========================================================================
// Xref link / unlink (Tier 2)
// ===========================================================================

const CreateXrefResultSchema = z.object({ ref_id: z.string(), is_live: z.boolean() }).passthrough();

export interface CreateXrefBody {
  criterion_id: string;
  source_module: 'contract_compliance' | 'health_check';
  source_entity_kind: 'deviation' | 'deliverable' | 'finding' | 'domain_rating';
  source_entity_id: string;
}

export function useCreateXrefMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }: { body: CreateXrefBody; idempotencyKey?: string }) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/readiness/cross-module-refs`,
        CreateXrefResultSchema,
        { method: 'POST', body, ...(idempotencyKey ? { idempotencyKey } : {}) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['readiness', 'xref', engagementId] });
    },
  });
}

const DetachXrefResultSchema = z.unknown();

export function useDetachXrefMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (refId: string) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/readiness/cross-module-refs/${encodeURIComponent(refId)}`,
        DetachXrefResultSchema,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['readiness', 'xref', engagementId] });
    },
  });
}
